import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../utilities/axios";
import {
  groupElementsByDate,
  makeElementsEmpty,
} from "../../utilities/groupElementsByDate";
import { groupMonths } from "../../utilities/regroupment";
import {
  addIdKeyToCategories,
  calculateMonthTotals,
  createArrayOfObjects,
  getElementsPerRange,
  updateSynthesisPlancompta,
} from "../../utilities/table";
import { addRacineToCatgories } from "../../utilities/tableUtils";
import { getElementsOfObjective, getOneObjective } from "./objectives";
import { openDeleteDialog } from "./table";

const initialState = {
  plancompta: [],
  plancomptaOriginal: [],
  plancomptaItem: null,
  levelOneNotMentioned: false,
  footerComptaFilter: { filterOptions: {} },
  footerOptionsTreso: { option: {}, account: {} },
  accountTreso: [],
  footerAccountTreso: { filterOptions: {} },
  footerOptionCompta: { option: {} },
  filterValuesCompta: [],
  plancomptaStatus: "idle",
  plancomptaForSelect: [],
  status: "idle",
  createStatus: "idle",
  isDragStarted: false,
  createError: null,
  error: null,
  synthesisArr: [],
  getSynthesisStatus: "idle",
  synthesis: [],
  editStatus: "idle",
  editError: "",
  plancomptaToEdit: "",
  footerFiltersStatus: {
    status: "pointé",
  },
  footerFiltersTypes: {
    type: "simulation,réel",
  },
  parentKey: "",
  simulationElement: null,
  isCreatingInSimulation: false,
};

export const getPlancompta = createAsyncThunk(
  "plancompta/getPlancompta",
  async (queries, thunkAPI) => {
    const nofilterChecked =
      !queries.params?.noFilterChecked &&
      queries.params?.filterOptions?.length === 0;

    if (queries.params) {
      delete queries?.params?.noFilterChecked;
    }

    let data;
    try {
      const response = await axios.get(
        `/api/projects/${queries.projectId}/plan-comptable`,
        {
          params: {
            ...queries.params,
          },
        }
      );

      data = await response.data;
      if (response.status === 200) {
        const arrayWithfilters = nofilterChecked
          ? makeElementsEmpty(data.data)
          : data.data;

        const transformedData = groupElementsByDate(
          arrayWithfilters,
          queries.mode
        );

        // Get values from categories slice
        const rootState = thunkAPI.getState();
        const categoriesRegroupmentValue =
          rootState.categories.RegroupmentValue;
        const categoriesSelectedMonths = rootState.categories.selectedMonths;

        return {
          data: transformedData,
          categoriesRegroupmentValue,
          categoriesSelectedMonths,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const getSynthesis = createAsyncThunk(
  "plancompta/getSynthesis",
  async (queries, thunkAPI) => {
    const nofilterChecked =
      !queries.params?.noFilterChecked &&
      queries?.params?.filterOptions?.length === 0;

    if (queries.params) {
      delete queries?.params?.noFilterChecked;
    }

    let data;
    try {
      const response = await axios.get(
        `/api/projects/${queries.projectId}/plan_comptable/${queries.mode}/synthese/`,
        { params: queries.params }
      );
      data = await response.data;

      if (
        queries?.mode === "compta" &&
        queries?.objectiveId &&
        !queries?.isListing
      ) {
        thunkAPI.dispatch(getElementsOfObjective(queries?.objective));
      }

      if (response.status === 200) {
        return nofilterChecked
          ? data.data.map((el) => (el.synthesis = 0))
          : data.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const getOnePlancompta = createAsyncThunk(
  "plancompta/getOnePlancompta",
  async (queries, thunkAPI) => {
    let data;
    try {
      const response = await axios.get(
        `api/plancompta/${queries.plancomptaId}/${queries.projectId}`
      );
      data = await response.data;
      if (response.status === 200) {
        return {
          data: data,
          message: data.message,
        };
      }
      throw new Error(response.statusText);
    } catch (err) {
      return Promise.reject(err.message ? err.message : err);
    }
  }
);

export const createPlancompta = createAsyncThunk(
  "plancompta/createPlancompta",
  async (plancomptaData, thunkAPI) => {
    let data;
    try {
      const response = await axios.post(
        `/api/projects/${plancomptaData?.project?.projectId}/plan-comptable`,
        plancomptaData?.values
      );
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(deleteInput());
        thunkAPI.dispatch(getPlancompta(plancomptaData?.project)).then(() => {
          thunkAPI.dispatch(getSynthesis(plancomptaData?.synthesis));

          if (
            plancomptaData?.mode === "compta" &&
            plancomptaData?.objectiveId &&
            !plancomptaData?.isListing
          ) {
            thunkAPI.dispatch(
              getElementsOfObjective(plancomptaData?.objectiveId)
            );
          }
        });
        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error({ err });
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const editPlancompta = createAsyncThunk(
  "plancompta/editPlancompta",
  async (values, thunkAPI) => {
    let data;
    try {
      const response = await axios.put(
        `/api/plan-comptable/${values.plancomptaId}`,
        values.values
      );

      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(getPlancomptaToEdit(""));
        thunkAPI.dispatch(getPlancompta(values.project)).then(() => {
          if (values.isSynthesis) {
            thunkAPI.dispatch(getSynthesis(values?.synthesis));
          }
          if (
            values?.mode === "compta" &&
            values?.objectiveId &&
            !values?.isListing
          ) {
            thunkAPI.dispatch(getElementsOfObjective(values?.objectiveId));
            thunkAPI.dispatch(getOneObjective(values?.objectiveId));
          }
        });

        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const deletePlancompta = createAsyncThunk(
  "plancompta/deletePlancompta",
  async (values, thunkAPI) => {
    let data;

    try {
      const response = await axios.delete(
        `/api/plan-comptable/${values.plancomptaId}/`
      );
      data = await response.data;
      if (response.status === 200) {
        thunkAPI.dispatch(openDeleteDialog(null));
        thunkAPI.dispatch(getPlancompta(values?.project)).then(() => {
          thunkAPI.dispatch(getSynthesis(values?.synthesis));

          if (
            values?.mode === "compta" &&
            values?.objectiveId &&
            !values?.isListing
          ) {
            thunkAPI.dispatch(getElementsOfObjective(values?.objectiveId));
          }
        });

        return data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      console.error({ err });
      return thunkAPI.rejectWithValue(err);
    }
  }
);

const slice = createSlice({
  name: "plancompta",
  initialState,
  reducers: {
    setTypeToFilter: (state, action) => {
      state.typeToFilter = action.payload;
    },
    setStatusToFilter: (state, action) => {
      state.statusTofilter = action.payload;
    },
    addInput: (state, action) => {
      state.plancomptaOriginal.push(action.payload);
    },
    setParentKey: (state, action) => {
      state.parentKey = action.payload;
    },
    setDragStarted: (state, action) => {
      state.isDragStarted = action.payload;
    },
    setAccountsTresoWithTypesAndStatus: (state, action) => {
      state.accountTreso = action.payload.accountTreso;
      state.noFilterChecked = action.payload.noFilterChecked;
    },
    setComptaFilters: (state, action) => {
      state.filterValuesCompta = action.payload.filterValuesCompta;
      state.noFilterCheckedCompta = action.payload.noFilterCheckedCompta;
    },
    setFooterAccountsTresoWithTypesAndStatus: (state, action) => {
      state.footerAccountTreso = action.payload.footerAccountTreso;
    },

    setFooterComptaFilter: (state, action) => {
      state.footerComptaFilter = action.payload;
    },

    setFooterComptaOptions: (state, action) => {
      state.footerOptionCompta = action.payload;
    },

    setFooterOptions: (state, action) => {
      state.footerOptionsTreso = action.payload;
    },
    resetFilters: (state, action) => {
      state.noFilterCheckedCompta = true;
      state.noFilterChecked = true;

      if (
        Object.keys(state.footerOptionsTreso.option).length > 0 ||
        Object.keys(state.footerOptionsTreso.account).length > 0
      ) {
        state.footerOptionsTreso = { option: {}, account: {} };
      }

      if (Object.keys(state.footerOptionCompta.option).length > 0) {
        state.footerOptionCompta = { option: {} };
      }

      if (Object.keys(state.footerComptaFilter.filterOptions).length > 0) {
        state.footerComptaFilter = { filterOptions: {} };
      }

      if (Object.keys(state.footerAccountTreso.filterOptions).length > 0) {
        state.footerAccountTreso = { filterOptions: {} };
      }

      if (state.accountTreso.length > 0) {
        state.accountTreso = [];
      }

      if (state.filterValuesCompta.length > 0) {
        state.filterValuesCompta = [];
      }
    },

    setFooterFilters: (state, action) => {
      if (action.payload.mode === "treso") {
        state.footerFiltersStatus = action.payload.filters;
        return;
      }
      state.footerFiltersTypes = action.payload.filters;
    },
    addInputWithCertainIndex: (state, action) => {
      function addObjectByIdInArray(arr, targetId, newObj) {
        for (const obj of arr) {
          if (obj.id === targetId) {
            if (!obj.children) {
              obj.children = [];
            }

            if (obj.children.length > 0) {
              const lastChild = obj.children[obj.children.length - 1];
              if (lastChild.isInput) {
                obj.children.pop();
                continue;
              }
            }
            obj.children.push(newObj);
          } else if (obj.children) {
            addObjectByIdInArray(obj.children, targetId, newObj);
          }
        }
      }

      return addObjectByIdInArray(
        state.plancomptaOriginal,
        action.payload.id,
        action.payload.newObj
      );
    },
    deleteInput: (state, action) => {
      if (
        state.plancomptaOriginal[state.plancomptaOriginal?.length - 1]?.isInput
      ) {
        state.plancomptaOriginal.pop();
      }
    },
    setLevelOneNotMentionedPlanCompta: (state, action) => {
      state.levelOneNotMentioned = action.payload;
    },
    getPlancomptaToEdit: (state, action) => {
      state.plancomptaToEdit = action.payload;
    },

    setPlancompta: (state, action) => {
      state.plancompta = action.payload;
      state.plancomptaOriginal = action.payload;
    },

    getPlancomptaState: (state) => {
      return state.plancompta;
    },

    addElementInPlancompta: (state, action) => {
      function addObjectByIdInArray(arr, targetId) {
        for (const obj of arr) {
          if (obj.id === targetId) {
            if (!obj.children) {
              obj.children = [];
            }

            let indexOfLastChildWithoutName = -1;

            const existingCreateChildIndex = obj.children.findIndex(
              (child) => child.isCreate
            );

            if (existingCreateChildIndex === -1) {
              state.isCreatingInSimulation = true;
              if (obj.children.length > 0) {
                for (let i = obj.children.length - 1; i >= 0; i--) {
                  const child = obj.children[i];
                  if (!child.name) {
                    indexOfLastChildWithoutName = i;
                    break;
                  }
                }
              }

              if (indexOfLastChildWithoutName === -1) {
                obj.children.splice(0, 0, {
                  dataIndex: action.payload.dataIndex,
                  plancompta: action.payload.plancompta,
                  isCreate: true,
                });
              }

              if (indexOfLastChildWithoutName !== -1) {
                obj.children.splice(indexOfLastChildWithoutName + 1, 0, {
                  dataIndex: action.payload.dataIndex,
                  plancompta: action.payload.plancompta,
                  isCreate: true,
                });
              }
            } else {
              state.isCreatingInSimulation = false;
              obj.children.splice(existingCreateChildIndex, 1);
            }
          } else if (obj.children) {
            addObjectByIdInArray(obj.children, targetId);
          }
        }
      }

      return addObjectByIdInArray(
        state.plancomptaOriginal,
        action.payload.plancompta.id
      );
    },
  },
  extraReducers: {
    [getPlancompta.pending]: (state, action) => {
      state.status = "loading";
    },
    [getPlancompta.fulfilled]: (state, action) => {
      state.status = "succeeded";
      const plancomptaWithKeys = addIdKeyToCategories(action.payload.data);

      const plancomptaWithKeysCopy = JSON.parse(
        JSON.stringify(plancomptaWithKeys)
      );

      state.plancomptaForSelect = plancomptaWithKeysCopy;

      const plancomptaWithRacine = addRacineToCatgories(plancomptaWithKeys);

      const monthsGrouped = groupMonths(
        action.payload.categoriesRegroupmentValue,
        action.payload.categoriesSelectedMonths
      );

      const plancomptaWithSum = calculateMonthTotals(
        plancomptaWithRacine,
        monthsGrouped
      );

      function processCategory(category) {
        const currentCategory = { ...category };
        if (currentCategory.elements) {
          const elementsPerRange = getElementsPerRange(
            monthsGrouped,
            currentCategory.elements
          );
          const finalElements = createArrayOfObjects(elementsPerRange);

          if (currentCategory.children) {
            const childrenArray = [...currentCategory.children];
            const reversedArrayElements = finalElements.toReversed();

            for (let i = 0; i < reversedArrayElements.length; i++) {
              const { children, ...category } = currentCategory;
              childrenArray.unshift({
                ...reversedArrayElements[i],
                id: `${currentCategory.id}-${i}`,
                category,
                isForelements: true,
                isMasked: category.isMasked,
              });
            }
            currentCategory.children = childrenArray.map((childCategory) =>
              processCategory(childCategory)
            );
          }
        }
        return currentCategory;
      }
      const processedPlancompta = plancomptaWithSum.map(processCategory);

      state.plancomptaOriginal = processedPlancompta;
      state.plancompta = processedPlancompta;
    },
    [getPlancompta.rejected]: (state, action) => {
      state.status = "failed";
      state.error = action.payload;
    },
    [getSynthesis.pending]: (state, action) => {
      state.synthesisStatus = "loading";
    },
    [getSynthesis.fulfilled]: (state, action) => {
      state.plancompta = updateSynthesisPlancompta(
        state.plancompta,
        action.payload
      );
      state.plancomptaOriginal = updateSynthesisPlancompta(
        state.plancompta,
        action.payload
      );

      state.synthesisStatus = "succeeded";
      state.synthesisArr = action.payload;
    },
    [getSynthesis.rejected]: (state, action) => {
      state.synthesisStatus = "failed";
      state.error = action.payload;
    },

    [getOnePlancompta.pending]: (state) => {
      state.plancomptaStatus = "loading";
    },
    [getOnePlancompta.fulfilled]: (state, action) => {
      state.plancomptaStatus = "succeeded";
      state.plancomptaItem = action.payload.data.data;
    },
    [getOnePlancompta.rejected]: (state) => {
      state.plancomptaStatus = "failed";
    },
    [createPlancompta.pending]: (state, action) => {
      state.createStatus = "loading";
    },
    [createPlancompta.fulfilled]: (state, action) => {
      state.createStatus = "succeeded";
    },
    [createPlancompta.rejected]: (state, action) => {
      state.createStatus = "failed";
      state.createError = action.payload;
    },
    [editPlancompta.pending]: (state, action) => {
      state.editStatus = "loading";
    },
    [editPlancompta.rejected]: (state, action) => {
      state.editStatus = "failed";
    },
  },
});

export const {
  setStatusToFilter,
  setTypeToFilter,
  addInput,
  deleteInput,
  getPlancomptaToEdit,
  addInputWithCertainIndex,
  setParentKey,
  setPlancompta,
  getPlancomptaState,
  addElementInPlancompta,
  setFilters,
  setDragStarted,
  resetFilters,
  setFooterComptaFilter,
  setFooterFilters,
  setFooterAccountsTresoWithTypesAndStatus,
  setAccountsTresoWithTypesAndStatus,
  setComptaFilters,
  setFooterOptions,
  setFooterComptaOptions,
  setLevelOneNotMentionedPlanCompta,
} = slice.actions;
export const reducer = slice.reducer;

export default slice;
